import { useMsal } from "@azure/msal-react";
import { Pivot, PivotItem } from "@fluentui/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";

import { ChatAppResponse, getDocument, getHeaders } from "../../api";
import { getToken, useLogin } from "../../authConfig";
import { EvidenceGraph, getEvidenceGraphAssessmentIds } from "../EvidenceGraph";
import { MarkdownViewer } from "../MarkdownViewer";
import { SupportingContent } from "../SupportingContent";
import styles from "./AnalysisPanel.module.css";
import { AnalysisPanelTabs } from "./AnalysisPanelTabs";
import { ThoughtProcess } from "./ThoughtProcess";

interface Props {
    className: string;
    activeTab: AnalysisPanelTabs;
    onActiveTabChanged: (tab: AnalysisPanelTabs) => void;
    activeCitation: string | undefined;
    citationHeight: string;
    answer: ChatAppResponse;
    onCitationClicked?: (citationFilePath: string) => void;
}

const pivotItemDisabledStyle = { disabled: true, style: { color: "grey" } };

export const AnalysisPanel = ({ answer, activeTab, activeCitation, citationHeight, className, onActiveTabChanged, onCitationClicked }: Props) => {
    const isDisabledThoughtProcessTab: boolean = !answer.context.thoughts;
    const dataPoints = answer.context.data_points;
    const hasSupportingContent = Boolean(
        dataPoints &&
            ((dataPoints.text && dataPoints.text.length > 0) ||
                (dataPoints.images && dataPoints.images.length > 0) ||
                (dataPoints.external_results_metadata && dataPoints.external_results_metadata.length > 0))
    );
    const isDisabledSupportingContentTab: boolean = !hasSupportingContent;
    const evidenceAssessmentIds = useMemo(() => getEvidenceGraphAssessmentIds(answer), [answer]);
    const effectiveCitation = activeCitation ?? evidenceAssessmentIds[0];
    const isDisabledCitationTab: boolean = !effectiveCitation;
    const [citation, setCitation] = useState("");
    const [idToken, setIdToken] = useState<string | undefined>();

    const client = useLogin ? useMsal().instance : undefined;
    const { t } = useTranslation();

    const [documentContent, setDocumentContent] = useState<any>(null);

    useEffect(() => {
        let cancelled = false;
        if (!client) {
            setIdToken(undefined);
            return;
        }
        getToken(client).then(token => {
            if (!cancelled) {
                setIdToken(token);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [client]);

    const isAssessmentId = (citation: string): boolean => {
        // AssessmentIds are numeric strings, file paths have extensions or slashes
        return /^\d+$/.test(citation) || !citation.includes(".");
    };

    const fetchCitation = async () => {
        if (effectiveCitation) {
            // Check if this is a clinical note (AssessmentId) or a file path
            const citationId = effectiveCitation.split("/").pop() || effectiveCitation;

            if (isAssessmentId(citationId)) {
                // Clinical note - use /document endpoint
                try {
                    const doc = await getDocument(citationId, idToken);
                    setDocumentContent(doc);
                    setCitation(""); // Clear blob URL
                } catch (error) {
                    console.error("Failed to fetch document:", error);
                }
            } else {
                // File from blob storage - use existing logic
                const originalHash = effectiveCitation.indexOf("#") ? effectiveCitation.split("#")[1] : "";
                const response = await fetch(effectiveCitation, {
                    method: "GET",
                    headers: await getHeaders(idToken)
                });
                const citationContent = await response.blob();
                let citationObjectUrl = URL.createObjectURL(citationContent);
                if (originalHash) {
                    citationObjectUrl += "#" + originalHash;
                }
                setCitation(citationObjectUrl);
                setDocumentContent(null); // Clear document content
            }
        }
    };
    useEffect(() => {
        fetchCitation();
    }, [effectiveCitation, idToken]);

    const renderFileViewer = () => {
        if (!effectiveCitation) {
            return null;
        }

        // If we have document content from /document endpoint, render it
        if (documentContent) {
            // Get highlights for this citation if available
            const citationHighlights = answer.context.data_points?.citation_highlights;
            const docId = documentContent.assessmentId || effectiveCitation?.split("/").pop() || "";
            const highlights = citationHighlights?.[docId] || [];

            return (
                <div style={{ padding: "20px" }}>
                    <h3>{documentContent.source || "Clinical Note"}</h3>
                    <p>
                        <strong>Note Type:</strong> {documentContent.noteType}
                    </p>
                    <p>
                        <strong>Date:</strong> {documentContent.assessmentDate}
                    </p>
                    {highlights.length > 0 && (
                        <div className={styles.highlightedExcerpts}>
                            <strong>Relevant Excerpts:</strong>
                            {highlights.map((h, i) => {
                                // Strip highlight markup (em/mark tags) but keep the text
                                const plain = h.replace(/<\/?(em|mark|b|strong)[^>]*>/gi, "");
                                return <p key={i} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(plain) }} />;
                            })}
                        </div>
                    )}
                    <hr />
                    <div style={{ whiteSpace: "pre-wrap" }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(documentContent.content) }} />
                </div>
            );
        }

        // Otherwise use existing file viewer logic
        const fileExtension = effectiveCitation.split(".").pop()?.toLowerCase();
        switch (fileExtension) {
            case "png":
                return <img src={citation} className={styles.citationImg} alt="Citation Image" />;
            case "md":
                return <MarkdownViewer src={effectiveCitation} />;
            default:
                return <iframe title="Citation" src={citation} width="100%" height={citationHeight} />;
        }
    };

    return (
        <Pivot
            className={className}
            selectedKey={activeTab}
            onLinkClick={pivotItem => pivotItem && onActiveTabChanged(pivotItem.props.itemKey! as AnalysisPanelTabs)}
        >
            <PivotItem
                itemKey={AnalysisPanelTabs.ThoughtProcessTab}
                headerText={t("headerTexts.thoughtProcess")}
                headerButtonProps={isDisabledThoughtProcessTab ? pivotItemDisabledStyle : undefined}
            >
                <ThoughtProcess thoughts={answer.context.thoughts || []} onCitationClicked={onCitationClicked} />
            </PivotItem>
            <PivotItem
                itemKey={AnalysisPanelTabs.SupportingContentTab}
                headerText={t("headerTexts.supportingContent")}
                headerButtonProps={isDisabledSupportingContentTab ? pivotItemDisabledStyle : undefined}
            >
                <SupportingContent supportingContent={answer.context.data_points} />
            </PivotItem>
            <PivotItem
                itemKey={AnalysisPanelTabs.CitationTab}
                headerText={t("headerTexts.citation")}
                headerButtonProps={isDisabledCitationTab ? pivotItemDisabledStyle : undefined}
            >
                {renderFileViewer()}
            </PivotItem>
            <PivotItem itemKey={AnalysisPanelTabs.EvidenceGraphTab} headerText={t("headerTexts.evidenceGraph")}>
                <EvidenceGraph assessmentIds={evidenceAssessmentIds} idToken={idToken} onCitationClicked={onCitationClicked} />
            </PivotItem>
        </Pivot>
    );
};
